import {
  notifyFormOpenedOccurred$,
  notifySubmitClickedOccurred$,
  notifyModalClosedOccurred$,
  notifyAbandonedOccurred$,
  notifySubmissionFailedOccurred$,
} from '../redux/trackingObservables';
import {
  startNotifySession,
  recordFieldInteraction,
  recordValidationErrors,
  markSubmitted,
  markSubmitAttempted,
  getElapsedSeconds,
  getFieldsCompletedCount,
  getNotifySessionSnapshot,
  resetNotifySession,
} from './getOnTheListTrackingState';
import { REALTOR_QUESTION_LABELS } from '../components/realtorDropdown';

export { recordFieldInteraction, recordValidationErrors };

// Fixed for every session this form starts — components/getOnTheListForm.js has a single CTA,
// unlike Contact Builder's book_appointment/contact_sales_center choice (leadFormTracking.js).
const BUTTON_NAME = 'Get on the list';

/**
 * Fires as soon as the "Get on the list" form is displayed — this is also where the session
 * clock starts (drives time_to_submit/time_spent later). Per NHS20-9093 this form now also
 * reports its own form_open/Lead Button Clicked event (the NHS20-8254 tracking it replaces didn't
 * have one).
 * @param {{ fromPage: string|undefined }} params
 */
export function trackNotifyFormOpened({ fromPage } = {}) {
  startNotifySession({ fromPage });
  notifyFormOpenedOccurred$.next({ buttonName: BUTTON_NAME, fromPage });
}

/**
 * Fires on a successful submit, right before the lead is actually posted.
 * @param {{ preferredContactMethod?: string, realtorQuestion?: string }} params
 */
export function trackNotifySubmitClicked({ preferredContactMethod, realtorQuestion } = {}) {
  const { started, fromPage } = getNotifySessionSnapshot();

  if (!started) {
    // No active session — defensive only, shouldn't happen since callers only invoke this after
    // trackNotifyFormOpened started one (see components/getOnTheListForm.js bindEvents).
    return;
  }

  const timeToSubmit = getElapsedSeconds();

  markSubmitted();

  // Per NHS20-9093, realtor_question is reported as the answer's label (e.g. "I am buying
  // without an agent"), not the raw '1'-'4' dropdown value — same fix as Contact Builder's
  // leadFormTracking.js.
  notifySubmitClickedOccurred$.next({
    buttonName: BUTTON_NAME,
    fromPage,
    preferredContactMethod,
    realtorQuestion: REALTOR_QUESTION_LABELS[realtorQuestion] ?? realtorQuestion,
    timeToSubmit,
  });
}

/**
 * Fires when a submit attempt fails. Only 'validation' is produced today — same scope limit as
 * Contact Builder's trackLeadFormSubmissionFailed. Also marks the session as submit-attempted — a
 * Submit click can fail validation with zero fields ever focused (e.g. prefilled fields left
 * untouched), which `formStarted` (focus-driven) alone wouldn't catch — so trackNotifyModalClosed
 * still treats it as an abandonable session if the user then closes without fixing it.
 * @param {string} errorType
 */
export function trackNotifySubmissionFailed(errorType) {
  const { started, fromPage } = getNotifySessionSnapshot();

  if (!started) {
    return;
  }

  markSubmitAttempted();

  notifySubmissionFailedOccurred$.next({ buttonName: BUTTON_NAME, fromPage, errorType });
}

function isFieldEmpty(formElement, fieldName) {
  return !formElement?.elements?.namedItem(fieldName)?.value;
}

/**
 * Fires on every modal close (X button, outside click, or Escape) while the form is mounted.
 * Also fires the abandonment Subject when the close represents a real abandonment — mirrors
 * Contact Builder's trackLeadFormClosed (leadFormTracking.js) exactly, since NHS20-9093 reuses
 * that same abandonment/validation-error semantics for this form.
 * @param {{ closeTarget: 'x_button'|'outside_click'|'escape', formElement: HTMLFormElement|null }} params
 */
export function trackNotifyModalClosed({ closeTarget, formElement }) {
  const {
    started, fromPage, formStarted, submitAttempted, submitted, lastFieldInteracted, errorFieldNames,
  } = getNotifySessionSnapshot();

  if (!started) {
    // No active session — defensive only, shouldn't happen since callers only invoke this while
    // their own form is mounted (see components/getOnTheListForm.js bindEvents).
    return;
  }

  const fieldsCompletedCount = getFieldsCompletedCount(formElement);

  notifyModalClosedOccurred$.next({
    buttonName: BUTTON_NAME, fromPage, closeTarget, formStarted, fieldsCompletedCount,
  });

  if ((formStarted || submitAttempted) && !submitted) {
    // Based on every field a failed submit flagged as invalid (errorFieldNames), not just
    // lastFieldInteracted — a Submit click can fail on fields the user never individually
    // focused (none populated, or only a few), so pinning this to "the last field touched" missed
    // required_missing whenever that field wasn't the (or wasn't among the) invalid one(s).
    const hasValidationError = errorFieldNames.size > 0;
    let errorType;
    if (hasValidationError) {
      const hasEmptyInvalidField = [...errorFieldNames].some((fieldName) => isFieldEmpty(formElement, fieldName));
      errorType = hasEmptyInvalidField ? 'required_missing' : 'invalid_format';
    }

    notifyAbandonedOccurred$.next({
      buttonName: BUTTON_NAME,
      fromPage,
      timeSpent: getElapsedSeconds(),
      lastFieldInteracted,
      fieldsCompletedCount,
      hasValidationError,
      errorType,
    });
  }

  resetNotifySession();
}
